/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { TVisitor } from "apis/visitors/type";
import { useRecoilValue } from "recoil";
import { userInfo, util } from "recoil/recoilState";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import InputField from "uis/InputField";
import Select from "uis/Select";
import { translate } from "./translate";

type TVisitorTable = {
    visitors: TVisitor[];
    setVisitors: (value: TVisitor[]) => void;
    language?: "KOR" | "ENG";
};

const TableContainer = styled.div`
    margin-top: 24px;
    display: flex;
    flex-direction: column;
`;

const HeaderContainer = styled.div`
    color: ${theme.neutral800};
    font-size: 18px;
    font-weight: 700;
    background-color: ${theme.neutral50};
    padding: 12px 24px;
    display: flex;
    gap: 12px;

    p {
        width: 150px;
    }

    .contact {
        flex: 1;
    }
`;

const RowContainer = styled.div`
    color: ${theme.neutral900};
    font-size: 16px;
    font-weight: 500;
    padding: 12px 24px;
    display: flex;
    gap: 12px;
    align-items: center;
    border-bottom: 1px solid ${theme.neutral100};

    div {
        width: 150px;
    }
`;

export function isValidPhoneNumber(number: string) {
    const regex = /^010\d{8}$/;
    return regex.test(number);
}

export const isValidEmail = (email: string) => {
    return email.match(
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const VisitorTable = (props: TVisitorTable) => {
    const { visitors, setVisitors, language = "KOR" } = props;

    return (
        <TableContainer>
            <TableHeader />
            {visitors.map((item, index) => {
                return (
                    <TableRow
                        index={index}
                        key={index}
                        visitor={item}
                        onChange={(value) => {
                            const temp = [...visitors];
                            temp[index] = value;
                            setVisitors(temp);
                        }}
                        language={language}
                        onDelete={() => setVisitors([...visitors].filter((_, i) => i !== index))}
                    />
                );
            })}
        </TableContainer>
    );
};

const TableHeader = () => {
    const utils = useRecoilValue(util);
    const headers = [
        "No.",
        translate["이름"][utils.language],
        translate["안내 채널"][utils.language],
        translate["전화번호/이메일"][utils.language],
    ];
    return (
        <HeaderContainer>
            {headers.map((item, index) => (
                <p className={item === translate["전화번호/이메일"][utils.language] ? "contact" : ""}>{item}</p>
            ))}
        </HeaderContainer>
    );
};

const TableRow = ({
    visitor,
    index,
    onChange,
    onDelete,
    language,
}: {
    visitor: TVisitor;
    index: number;
    onChange?: (value: TVisitor) => void;
    onDelete?: () => void;
    language: "KOR" | "ENG";
}) => {
    const { guideChannel, name, phoneNumber, email } = visitor;

    return (
        <RowContainer>
            <div>{index + 1}</div>
            <NameInput
                value={name}
                onChange={(value: string) => {
                    onChange?.({ ...visitor, name: value });
                }}
            />
            <SelectChannel
                value={guideChannel}
                onChange={(value) => onChange?.({ ...visitor, guideChannel: value })}
                data={language === "ENG" ? ["email"] : ["kakao", "email"]}
            />
            {guideChannel === "EMAIL" && (
                <ContactInput
                    value={email ? email : undefined}
                    channel={guideChannel}
                    onChange={(value) => {
                        onChange?.({ ...visitor, email: value });
                    }}
                />
            )}
            {guideChannel === "KAKAO" && (
                <ContactInput
                    value={phoneNumber ? phoneNumber : undefined}
                    channel={guideChannel}
                    onChange={(value) => {
                        onChange?.({ ...visitor, phoneNumber: value });
                    }}
                />
            )}
            <Icon name="delete" color={theme.neutral400} hoverColor={theme.neutral500} onClick={onDelete} />
        </RowContainer>
    );
};

const NameInput = ({ value, onChange }: { value?: string; onChange?: (value: string) => void }) => {
    const { language } = useRecoilValue(util);

    return (
        <InputField
            onChange={(value) => {
                onChange?.(value);
            }}
            placeholder={translate["이름"][language]}
            defaultValue={value}
            value={value}
            styles={{
                container: css`
                    padding: 0;
                `,
                input: css`
                    font-size: 16px;
                    font-weight: 500;
                    background-color: white;
                    border-radius: 0;
                    padding: 0;
                `,
            }}
        />
    );
};

const ContactInput = ({
    value,
    channel,
    onChange,
}: {
    value?: string;
    channel?: "EMAIL" | "KAKAO";
    onChange?: (value: string) => void;
}) => {
    const { language } = useRecoilValue(util);
    const isValidate = () => {
        return (channel === "EMAIL" && isValidEmail(`${value}`)) || (channel === "KAKAO" && isValidPhoneNumber(`${value}`)) || !value;
    };

    return (
        <InputField
            onChange={(item) => {
                if (channel === "KAKAO") {
                    onChange?.(item.replaceAll("-", ""));
                } else onChange?.(item);
            }}
            value={value}
            placeholder={
                channel === "EMAIL" ? translate["이메일을 입력해주세요."][language] : translate["전화번호를 - 없이 입력해주세요."][language]
            }
            styles={{
                container: css`
                    flex: 1;
                `,
                input: css`
                    font-size: 16px;
                    font-weight: 500;

                    background-color: ${isValidate() ? "white" : "#FF00000D"};
                    border-radius: 0;
                    padding: 8px;
                `,
            }}
        />
    );
};

const SelectChannel = ({
    data = ["kakao", "email"],
    value = "KAKAO",
    onChange = () => {},
}: {
    value: "EMAIL" | "KAKAO";
    onChange?: (value: "EMAIL" | "KAKAO") => void;
    data?: string[];
}) => {
    return (
        <Select
            data={data}
            id={"1"}
            selectedData={[value?.toLowerCase()]}
            onChange={(value) => onChange(value[0] === "kakao" ? "KAKAO" : "EMAIL")}
            width="150px"
            styles={{
                container: css`
                    padding: 8px 0;
                    border-radius: 0px;
                    background-color: white;
                `,
                label: css`
                    margin-left: 8px;
                `,
                iconContainer: css`
                    margin-right: 8px;
                    width: min-content !important;
                `,
            }}
        />
    );
};
export default VisitorTable;
